import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../config/apiConfig';
import { getPasswordEndpoints } from '../../utils/passwordEndpoints';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const VendorSetPassword = () => {
  const navigate = useNavigate();
  const storedVendor = (() => {
    try {
      return JSON.parse(localStorage.getItem('vendorData') || 'null');
    } catch (e) {
      return null;
    }
  })();

  const [email, setEmail] = useState(storedVendor?.email || '');
  const { sendOtpUrl, resetUrl } = getPasswordEndpoints('vendor');
  const token = localStorage.getItem('vendorToken');
  const mustSetLocal = localStorage.getItem('vendorMustSetPassword') === 'true';
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(token && mustSetLocal ? 2 : 1); // 1: send otp, 2: verify otp + set password
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    // If email is present from previous flow, auto-send OTP (optional)
  }, []);

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    if (!email || !email.trim()) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(sendOtpUrl, { email: email.trim() });
      if (res.data && (res.data.status || res.data.success)) {
        toast.success(res.data.message || 'OTP sent to your email');
        setStep(2);
      } else {
        toast.error(res.data?.message || 'Failed to send OTP');
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to send OTP';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      const res = await axios.post(sendOtpUrl, { email: email.trim() });
      if (res.data && (res.data.status || res.data.success)) {
        toast.success(res.data.message || 'OTP resent to your email');
      } else {
        toast.error(res.data?.message || 'Failed to resend OTP');
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to resend OTP';
      toast.error(message);
    } finally {
      setResendLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    // Validation
    if (!newPassword || newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirm password do not match');
      return;
    }

    setLoading(true);
    try {
      // If vendor is authenticated from OTP flow, use protected updatepassword endpoint without requiring OTP
      const token = localStorage.getItem('vendorToken');
      const mustSetLocal = localStorage.getItem('vendorMustSetPassword') === 'true';

      if (token && mustSetLocal) {
        const res = await axios.put(`${API_URL}/vendor/updatepassword`, { newPassword }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.data && (res.data.status || res.data.success)) {
          toast.success(res.data.message || 'Password set successfully. Please login.');
          // Clear the temporary flag
          localStorage.removeItem('vendorMustSetPassword');
          // Remove vendor token to force fresh login with password
          localStorage.removeItem('vendorToken');
          navigate('/vendor/login');
        } else {
          toast.error(res.data?.message || 'Failed to set password');
        }
      } else {
        // Fallback to OTP-based reset
        if (!otp || otp.trim().length === 0) {
          toast.error('Please enter the OTP');
          setLoading(false);
          return;
        }

        const res = await axios.post(resetUrl, {
          email: email.trim(),
          otp: otp.trim(),
          newPassword
        });

        if (res.data && (res.data.status || res.data.success)) {
          toast.success(res.data.message || 'Password set successfully. Please login.');
          navigate('/vendor/login');
        } else {
          toast.error(res.data?.message || 'Failed to set password');
        }
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to set password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="w-full max-w-md p-6 bg-white rounded-lg shadow">
        <h2 className="text-2xl font-bold mb-2">Set Your Password</h2>
        <p className="text-sm text-gray-600 mb-6">If this is your first login, verify your email with the OTP and set a new password.</p>

        {step === 1 && (
          <form onSubmit={handleSendOtp} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 block w-full border rounded p-2"
                placeholder="Enter your registered email"
              />
            </div>

            <div className="flex justify-end">
              <button type="submit" disabled={loading} className={`px-4 py-2 rounded ${loading ? 'bg-gray-400' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
                {loading ? 'Sending...' : 'Send OTP'}
              </button>
            </div>

            <div className="mt-4 text-sm">
              Remembered your password? <Link to="/vendor/login" className="text-indigo-600 underline">Login</Link>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleSetPassword} className="space-y-4">
            {!(token && mustSetLocal) && (
              <div>
                <label className="block text-sm font-medium text-gray-700">One-time Code (OTP)</label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  maxLength={6}
                  required
                  className="mt-1 block w-full border rounded p-2 text-center tracking-widest font-bold"
                  placeholder="Enter 6-digit code"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700">New Password</label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="mt-1 block w-full border rounded p-2 pr-10"
                  placeholder="Choose a new password"
                />
                <button type="button" onClick={() => setShowNewPassword(prev => !prev)} className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 focus:outline-none">
                  {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Confirm New Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="mt-1 block w-full border rounded p-2 pr-10"
                  placeholder="Re-enter new password"
                />
                <button type="button" onClick={() => setShowConfirmPassword(prev => !prev)} className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 focus:outline-none">
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div>
                <button type="button" onClick={() => { setStep(1); setOtp(''); }} className="text-sm text-gray-600 underline">Change email</button>
              </div>

              <div className="flex items-center space-x-2">
                {!(token && mustSetLocal) && (
                  <button type="button" onClick={handleResend} disabled={resendLoading} className="text-sm text-indigo-600 underline disabled:opacity-50">{resendLoading ? 'Resending...' : 'Resend Code'}</button>
                )}

                <button type="submit" disabled={loading} className={`px-4 py-2 rounded ${loading ? 'bg-gray-400' : 'bg-green-600 text-white hover:bg-green-700'}`}>
                  {loading ? 'Setting...' : 'Set Password'}
                </button>
              </div>
            </div>

            <div className="mt-4 text-sm">
              After setting password, <Link to="/vendor/login" className="text-indigo-600 underline">login here</Link>.
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default VendorSetPassword;
